import type { Supply } from './api';
import { tieneModulo, type Negocio } from './negocio';

/**
 * Qué enseña la portada sobre el stock: lo que hay que reponer, lo que se
 * vendió de más, o que todavía no se contó nada.
 *
 * El 8 de octubre el dueño vio «41 de 41 en nivel crítico» y una reposición
 * sugerida de «0 unid» en una tienda de ropa recién abierta: lo vendido tal
 * cual nace en el almacén con cero y sin mínimo, y la portada lo contaba todo
 * como «bajo mínimo». Aquí se decide una sola vez; la regla de qué está bajo
 * mínimo vive en el servidor (`isBelowMinimum`), la misma del correo y de «Qué
 * comprar».
 */
export type EstadoDelStock =
  | {
      /** Nunca se registró nada: una sola tarjeta en vez de un muro de filas. */
      tipo: 'sin-registrar';
      vendidoDeMas: number;
    }
  | {
      tipo: 'registrado';
      /** Bajo su mínimo y con algo que pedir: lo que de verdad hay que comprar. */
      aReponer: Supply[];
      /** En negativo: se vendió más de lo que se registró. */
      vendidoDeMas: Supply[];
      /**
       * Se acabó y no tiene mínimo. No es una compra sugerida —sin mínimo no
       * hay cuánto pedir— pero se enseña: el 8 de octubre el pescado del
       * chifa, sin mínimo, dejaba de verse en la portada el día que se acababa.
       */
      agotadosSinMinimo: Supply[];
    };

export function estadoDelStock(
  /** Nulo: un servidor de antes, que no lo dice. Se toma como registrado. */
  stockRecorded: boolean | undefined,
  insumos: readonly Supply[],
): EstadoDelStock {
  const vendidoDeMas = insumos.filter((insumo) => insumo.oversold === true);

  if (stockRecorded === false) {
    return { tipo: 'sin-registrar', vendidoDeMas: vendidoDeMas.length };
  }

  return {
    tipo: 'registrado',
    // Sin mínimo no hay reposición, y una sugerencia de cero no es una
    // compra: antes salían filas que decían «pedir 0 unid».
    aReponer: insumos
      .filter((insumo) => bajoMinimo(insumo) && Number(insumo.suggestedRestock) > 0)
      .sort((a, b) => urgencia(a.level) - urgencia(b.level)),
    vendidoDeMas,
    agotadosSinMinimo: insumos.filter((insumo) => agotadoSinMinimo(insumo)),
  };
}

/**
 * Agotado sin mínimo según el servidor. Con uno de antes, que no lo manda, lo
 * que está en cero sin mínimo y no en negativo.
 */
function agotadoSinMinimo(insumo: Supply): boolean {
  if (insumo.outWithoutMinimum !== undefined) return insumo.outWithoutMinimum;
  return insumo.level === 'out' && insumo.oversold !== true && Number(insumo.minimum) === 0;
}

/**
 * Bajo mínimo según el servidor; con uno de antes, que no lo manda, por el
 * nivel como siempre.
 */
function bajoMinimo(insumo: Supply): boolean {
  return insumo.belowMinimum ?? (insumo.level !== 'ok' && insumo.level !== 'over');
}

/** Lo que deja al local sin vender primero. */
function urgencia(level: string): number {
  return level === 'out' ? 0 : level === 'critical' ? 1 : 2;
}

/**
 * Por dónde se empieza a registrar lo que hay, según lo que el local tiene.
 *
 * La carga inicial —una hoja con todo de una vez— solo existe con el módulo de
 * presentaciones (ver `menu.ts`); recibir y contar los tiene cualquiera.
 */
export function comoRegistrarElStock(
  negocio: Negocio,
): { texto: string; href: string }[] {
  return [
    ...(tieneModulo(negocio, 'presentations')
      ? [{ texto: 'Cargar desde una hoja', href: '#/carga-inicial' }]
      : []),
    { texto: 'Registrar lo que llegó', href: '#/recepcion' },
    { texto: 'Contar el estante', href: '#/conteo' },
  ];
}

/**
 * En qué filtro de la lista cae un producto.
 *
 * «Agotado» es lo que tuvo stock y se acabó. Lo que nunca se registró —la
 * ferretería que empezó vendiendo y tiene 2008 productos en cero— va a «Sin
 * contar»: con los dos juntos, «Agotados 2008» escondía los diez que de verdad
 * se acabaron (8 oct, segunda ronda del almacén). Lo vendido de más es «Agotado»
 * si alguna vez se registró: falta un ingreso o un conteo, y es lo que urge.
 */
export type FiltroDeLista = Supply['level'] | 'sin-contar';

export function filtroDeLista(insumo: Supply): FiltroDeLista {
  if (insumo.everStocked === false && Number(insumo.quantity) <= 0) return 'sin-contar';
  return insumo.level;
}
